import { ChartInsight } from '@api';
import { EventsConfig } from '@chart';
import { ChartV2Insight } from '@chartv2';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { getInsightEntityTag } from '@terminal/views/turbo-chart/hooks/use-insights-in-domain/utils';
import type { TerminalPaneData } from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { createElement, ReactNode } from 'react';

import { ChartV2InsightIcon } from '../components/ChartV2InsightIcon';

const DEFAULT_PANE_ID_PREFIX = 'pane-';

interface InsightBucket {
  date: Date;
  paneIndex: number;
  insights: ChartInsight[];
}

/**
 * Build a map from entity tag → pane index using the current pane layout,
 * so we can attach each insight to its owning pane.
 */
export const buildEntityPaneIndex = (
  chartPanes: TerminalPaneData[]
): Map<string, number> => {
  const map = new Map<string, number>();

  chartPanes.forEach((pane, paneIndex) => {
    pane.chartAssetData.forEach(asset => {
      if (!map.has(asset.entity.tag)) {
        map.set(asset.entity.tag, paneIndex);
      }
    });
  });

  return map;
};

/**
 * Filters the raw insight feed by per-entity toggles from `eventsConfig`
 * (when TMI is on), mirroring the original `Insights` component's behaviour.
 */
export const filterByEventsConfig = (
  insights: ChartInsight[],
  eventsConfig: EventsConfig
): ChartInsight[] => {
  if (!eventsConfig.TMI) {
    return [];
  }

  if (!eventsConfig.INSIGHTS) {
    return insights;
  }

  return insights.filter(insight => {
    const entityTag = getInsightEntityTag(insight);

    return eventsConfig.INSIGHTS?.[entityTag];
  });
};

/**
 * Group insights by (paneIndex, day) so insights that belong to different
 * panes but happen to share a date don't get merged into a single icon
 * stack on the primary pane.
 */
export const groupByPaneAndDate = (
  insights: ChartInsight[],
  entityPaneIndex: Map<string, number>
): Record<string, InsightBucket> => {
  const grouped: Record<string, InsightBucket> = {};

  for (const insight of insights) {
    const entityTag = getInsightEntityTag(insight);
    const paneIndex = entityPaneIndex.get(entityTag) ?? 0;
    const dateKey = insight.created_at.split('T')[0];
    const bucketKey = `${paneIndex}:${dateKey}`;
    const existing = grouped[bucketKey];

    if (existing) {
      existing.insights.push(insight);
      continue;
    }

    grouped[bucketKey] = {
      date: new Date(dateKey),
      paneIndex,
      insights: [insight],
    };
  }

  return grouped;
};

export const toChartV2Insights = (
  insights: ChartInsight[],
  entityPaneIndex: Map<string, number>,
  options?: { small?: boolean; source?: PosthogTrackingSource }
): ChartV2Insight[] => {
  if (insights.length === 0) {
    return [];
  }

  const { small, source } = options ?? {};
  const grouped = groupByPaneAndDate(insights, entityPaneIndex);

  return Object.entries(grouped).map(([bucketKey, bucket]) => ({
    id: `chartv2-insight-${bucketKey}`,
    time: bucket.date.getTime(),
    paneId: `${DEFAULT_PANE_ID_PREFIX}${bucket.paneIndex}`,
    icons: bucket.insights.map(
      (insight): ReactNode =>
        createElement(ChartV2InsightIcon, {
          key: insight.id,
          insight,
          small,
          source,
        })
    ),
  }));
};
